import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { DragDropContext, DropResult, Droppable } from 'react-beautiful-dnd'
import { toast } from 'react-toastify'

import { CustomTable } from 'common/components/custom-table/CustomTable'
import { LoadingOverlay } from 'common/components/loading-overlay/LoadingOverlay'
import { Playlist } from 'features/playlists/service'
import { videosTableData } from 'features/videos/data'
import { Video, VideoSortField, useChangeVideosOrderMutation } from 'features/videos/service'
import { Course } from 'services/courses'

import { VideosTableRow } from './videos-table-row/VideosTableRow'

type Props = {
  courseId: number | string
  courses?: Course[]
  playlistId: string
  playlists?: Playlist[]
  setSettingIds: Dispatch<SetStateAction<string[]>>
  settingIds: string[]
  sortField: VideoSortField
  videosData?: Video[]
}

export const VideosTable = ({
  courseId,
  courses,
  playlistId,
  playlists,
  setSettingIds,
  settingIds,
  sortField,
  videosData,
}: Props) => {
  const [videos, setVideos] = useState(videosData)

  const [changeVideosOrder, { isLoading }] = useChangeVideosOrderMutation()

  // Drag-n-drop меняет ручной порядок (VideoSettings.order). Он завязан на ручной
  // порядок как таковой (sort=default), а НЕ на направление: фронт шлёт абсолютный
  // order соседа в точке броска (см. destinationItemOrder ниже), а бэк перекладывает
  // order глобально по всему плейлисту и симметричен к asc/desc — поэтому DnD корректен
  // в обоих направлениях. Выключаем DnD только для sort=created, где ручного порядка нет.
  const isManualOrder = sortField === 'default'

  const handleDragEnd = (result: DropResult) => {
    if (!isManualOrder) {
      return
    }
    if (!result.destination) {
      return
    }

    const items = Array.from(videos ?? [])

    const destinationItemOrder = items[result.destination.index].order

    const [reorderedItem] = items.splice(result.source.index, 1)

    items.splice(result.destination.index, 0, reorderedItem)

    setVideos(items)

    changeVideosOrder({
      courseId,
      order: destinationItemOrder,
      playlistId,
      videoId: result.draggableId,
    })
      .unwrap()
      .then(res => {
        if (res) {
          if (res.response.statusCode === 404) {
            toast.error(res.message || 'Произошла ошибка')
            setVideos(videosData)
          }
        }
      })
      .catch(error => {
        toast.error(error.data.message || 'Произошла ошибка')
        setVideos(videosData)
      })
  }

  useEffect(() => {
    setVideos(videosData)
  }, [videosData])

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <CustomTable
          onChange={() => {}}
          sortColumn={''}
          sortDirection={''}
          tableData={videosTableData}
        >
          <Droppable droppableId={'videos'}>
            {provided => {
              return (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {videos?.map((video, index) => (
                    <VideosTableRow
                      courseId={courseId}
                      courses={courses}
                      dragDisabledBySort={!isManualOrder}
                      index={index}
                      key={video.id}
                      playlistId={playlistId}
                      playlists={playlists}
                      setSettingIds={setSettingIds}
                      settingIds={settingIds}
                      video={video}
                      hasMainMdxNote={video.hasMainMdxNote}
                    />
                  ))}
                  {provided.placeholder}
                </div>
              )
            }}
          </Droppable>
        </CustomTable>
      </DragDropContext>

      <LoadingOverlay open={isLoading} />
    </>
  )
}
